import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  CircularProgress,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton,
  Tooltip,
  Stack,
  Card,
  CardContent,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  TablePagination,
  InputAdornment
} from '@mui/material';
import { format } from 'date-fns';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  Block as DeactivateIcon,
  RestartAlt as ReactivateIcon,
  Key as ResetPasswordIcon,
  Edit as EditIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useTheme, useMediaQuery } from '@mui/material';

const UserManagement = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [editUserDialog, setEditUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editData, setEditData] = useState({
    first_name: '',
    last_name: '',
    phone_number: '',
    email: '',
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    gender: '',
    languages: '',
    service_unit: '',
    role: 'unit_member'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  
  // Check if current user is super-admin (no organization_id)
  const isSuperAdmin = user?.role === 'system_admin' && !user?.organization_id;

  const handleSessionExpired = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login', { state: { message: 'Your session has expired. Please log in again.' } });
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let url = `${process.env.REACT_APP_API_URL}/api/auth/users?page=${page + 1}&limit=${rowsPerPage}`;
      
      if (debouncedSearchTerm) {
        url += `&search=${encodeURIComponent(debouncedSearchTerm)}`;
      }

      const response = await fetch(
        url,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return;
      }

      const result = await response.json();
      if (response.ok) {
        setUsers(result.data);
        setTotalCount(result.total);
      } else {
        setError(result.error || 'Failed to fetch users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, debouncedSearchTerm]);

  // Add debounce effect for search
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const handleUserAction = async (userId, action) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/update-status/${userId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            status: action === 'approve' ? 'active' : 'rejected'
          })
        }
      );

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update user status');
      }

      fetchUsers();
      setSuccess(`User successfully ${action === 'approve' ? 'approved' : 'rejected'}`);
    } catch (error) {
      setError(`Failed to ${action} user`);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/change-role/${userId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ role: newRole })
        }
      );

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message);
        fetchUsers();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error);
      }
    } catch (error) {
      setError('Failed to update user role');
    }
  };

  const handleResetPassword = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/reset-password/${selectedUser.id}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ newPassword })
        }
      );

      if (response.status === 401) {
        handleSessionExpired();
        return;
      }

      const data = await response.json();
      if (response.ok) {
        setSuccess('Password reset successfully');
        setResetPasswordDialog(false);
        setNewPassword('');
        setSelectedUser(null);
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error);
      }
    } catch (error) {
      setError('Failed to reset password');
    }
  };

  const handleEditUser = async () => {
    try {
      console.log('Sending update data:', editData);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/update-user/${selectedUser.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            first_name: editData.first_name,
            last_name: editData.last_name,
            email: editData.email,
            phone_number: editData.phone_number,
            address_line1: editData.address_line1,
            address_line2: editData.address_line2,
            city: editData.city,
            postal_code: editData.postal_code,
            gender: editData.gender,
            languages: editData.languages,
            service_unit: editData.service_unit,
            role: editData.role,
          })
        }
      );

      const data = await response.json();
      if (response.ok) {
        setSuccess('User updated successfully');
        setEditUserDialog(false);
        fetchUsers(); // Refresh the list
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.error);
      }
    } catch (error) {
      console.error('Update error:', error);
      setError('Failed to update user');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      case 'inactive':
        return 'default';
      default:
        return 'default';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page on new search
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ width: '100%' }}>
      <Paper sx={{ p: 2, my: 3, overflow: 'hidden' }}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Name, Email, Phone"
                sx={{ width: { xs: '100%', sm: 250 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>

            <TableContainer 
              component={Paper}
              sx={{ 
                width: '100%',
                overflowX: 'auto',
                '& .MuiTableCell-root': {
                  whiteSpace: 'nowrap',
                  padding: '8px 12px'
                }
              }}
            >
              <Table sx={{ width: '100%', tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ minWidth: 120 }}>Full Name</TableCell>
                    <TableCell sx={{ minWidth: 200, maxWidth: 250 }}>Email</TableCell>
                    <TableCell sx={{ minWidth: 100 }}>Role</TableCell>
                    {isSuperAdmin && <TableCell sx={{ minWidth: 150 }}>Branch</TableCell>}
                    <TableCell sx={{ minWidth: 120 }}>Service Unit</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Status</TableCell>
                    <TableCell sx={{ minWidth: 140 }}>Created On</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((userItem) => (
                    <TableRow key={userItem.id}>
                      <TableCell>
                        {userItem.first_name} {userItem.last_name}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={userItem.email}
                      >
                        {userItem.email}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={
                            userItem.role === 'system_admin' && !userItem.organization_id ? 'Super Admin' :
                            userItem.role === 'system_admin' && userItem.organization_id ? 'Organization Admin' :
                            userItem.role === 'unit_manager' ? 'Unit Manager' :
                            'Unit Member'
                          }
                          color={
                            userItem.role === 'system_admin' && !userItem.organization_id ? 'error' :
                            userItem.role === 'system_admin' && userItem.organization_id ? 'primary' :
                            userItem.role === 'unit_manager' ? 'secondary' :
                            'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell>
                          {userItem.organization_name ? (
                            <Chip 
                              label={userItem.organization_name}
                              variant="outlined"
                              size="small"
                              color="primary"
                            />
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              N/A
                            </Typography>
                          )}
                        </TableCell>
                      )}
                      <TableCell>
                        {userItem.service_unit ? (
                          <Chip 
                            label={userItem.service_unit.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            variant="outlined"
                            size="small"
                          />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not Assigned
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={userItem.status}
                          color={getStatusColor(userItem.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.875rem' }}>
                        {format(new Date(userItem.created_at), 'MMM d, yyyy h:mm a')}
                      </TableCell>
                      <TableCell>
                        {userItem.status === 'pending' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                color="success"
                                onClick={() => handleUserAction(userItem.id, 'approve')}
                                size="small"
                              >
                                <ApproveIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                onClick={() => handleUserAction(userItem.id, 'reject')}
                                size="small"
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {userItem.status === 'active' && (
                          <Tooltip title="Deactivate">
                            <IconButton
                              color="error"
                              onClick={() => handleUserAction(userItem.id, 'deactivate')}
                              size="small"
                            >
                              <DeactivateIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {userItem.status === 'inactive' && (
                          <Tooltip title="Reactivate">
                            <IconButton
                              color="success"
                              onClick={() => handleUserAction(userItem.id, 'approve')}
                              size="small"
                            >
                              <ReactivateIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Reset Password">
                          <IconButton
                            onClick={() => {
                              setSelectedUser(userItem);
                              setResetPasswordDialog(true);
                            }}
                            size="small"
                          >
                            <ResetPasswordIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit User">
                          <IconButton
                            onClick={() => {
                              setSelectedUser(userItem);
                              setEditData({
                                first_name: userItem.first_name || '',
                                last_name: userItem.last_name || '',
                                phone_number: userItem.phone_number || '',
                                email: userItem.email,
                                address_line1: userItem.address_line1 || '',
                                address_line2: userItem.address_line2 || '',
                                city: userItem.city || '',
                                postal_code: userItem.postal_code || '',
                                gender: userItem.gender || '',
                                languages: userItem.languages || '',
                                service_unit: userItem.service_unit || '',
                                role: userItem.role || 'unit_member'
                              });
                              setEditUserDialog(true);
                            }}
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Box>
          </>
        )}
      </Paper>

      <Dialog open={resetPasswordDialog} onClose={() => setResetPasswordDialog(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Resetting password for: {selectedUser?.email}
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="New Password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialog(false)}>Cancel</Button>
          <Button onClick={handleResetPassword} variant="contained" color="primary">
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={editUserDialog} 
        onClose={() => setEditUserDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={editData.first_name}
                onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={editData.last_name}
                onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                value={editData.email}
                onChange={(e) => setEditData({ ...editData, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={editData.phone_number}
                onChange={(e) => setEditData({ ...editData, phone_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 1"
                value={editData.address_line1}
                onChange={(e) => setEditData({ ...editData, address_line1: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address Line 2"
                value={editData.address_line2}
                onChange={(e) => setEditData({ ...editData, address_line2: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={editData.city}
                onChange={(e) => setEditData({ ...editData, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Postal Code"
                value={editData.postal_code}
                onChange={(e) => setEditData({ ...editData, postal_code: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <FormLabel>Gender</FormLabel>
                <RadioGroup
                  row
                  value={editData.gender}
                  onChange={(e) => setEditData({ ...editData, gender: e.target.value })}
                >
                  <FormControlLabel value="male" control={<Radio />} label="Male" />
                  <FormControlLabel value="female" control={<Radio />} label="Female" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Languages Spoken"
                value={editData.languages}
                onChange={(e) => setEditData({ ...editData, languages: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Role</InputLabel>
                <Select
                  value={editData.role}
                  onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                >
                  <MenuItem value="system_admin">
                    {selectedUser?.organization_id ? 'Organization Admin' : 'Super Admin'}
                  </MenuItem>
                  <MenuItem value="unit_manager">Unit Manager</MenuItem>
                  <MenuItem value="unit_member">Unit Member</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Service Unit</InputLabel>
                <Select
                  value={editData.service_unit}
                  onChange={(e) => setEditData({ ...editData, service_unit: e.target.value })}
                >
                  <MenuItem value="pastorate">Pastorate</MenuItem>
                  <MenuItem value="hospitality">Hospitality</MenuItem>
                  <MenuItem value="follow_up">Follow Up</MenuItem>
                  <MenuItem value="homecell">Homecell</MenuItem>
                  <MenuItem value="nehemiah_squad">Nehemiah Squad</MenuItem>
                  <MenuItem value="call_center">Call Center</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUserDialog(false)}>Cancel</Button>
          <Button onClick={handleEditUser} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default UserManagement; 